/**
 * @file inboxPaths.ts
 * @brief Where a notification opens on the web: its `target` → a page under /app, /company or /admin.
 *
 * @details
 * Same mapping as the desktop app's main process for a clicked notification
 * (DESKTOP src/notifications.js pathFor); keep the two in step. Pure, so it
 * is tested with node --test (inboxPaths.test.ts).
 *
 * @author DES
 * @date 12-10-2026
 * @updated 13-10-2026 — DES: `/admin` (a super admin's Inbox).
 */

export type InboxTarget = { type?: string; id?: string; tab?: string };

/** A notification's page on the web (desktop notifications open the same). */
export function webPathFor(target: InboxTarget | null | undefined, base: "/admin" | "/app" | "/company"): string {
  const t = target ?? {};
  const id = typeof t.id === "string" && t.id ? encodeURIComponent(t.id) : "";
  switch (t.type) {
    case "meeting":
      return id ? `${base}/meetings/${id}${t.tab === "todos" ? "?tab=todos" : ""}` : `${base}/meetings`;
    case "note":
      return id ? `${base}/notes/${id}` : `${base}/notes`;
    case "scheduled_meeting":
      return `${base}/meetings/scheduled`;
    case "scheduled_session":
      return `${base}/sessions`;
    // "See plans" on the minutes 80% / 100% pushes (18-10-2026): web and Mac
    // have their own Billing, so it opens the plan screen (no store rules here).
    case "plans":
      return base === "/company" ? "/company/billing" : base === "/app" ? "/app/billing/plan" : `${base}/home`;
    case "billing":
      // A user's own Billing exists on the web since 17-10-2026.
      return base === "/company" ? "/company/billing" : base === "/app" ? "/app/billing/plan" : `${base}/account`;
    case "security":
      return `${base}/account`;
    case "meetings":
      return `${base}/meetings`;
    default:
      return base === "/app" ? "/app" : `${base}/home`;
  }
}
